import {Trans, useTranslation} from "react-i18next";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import {
    Alert,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    Link,
    OutlinedInput,
} from "@mui/material";
import {IRegisterUserBody} from "../../../../store/auth/Helper";
import React, {useEffect, useState} from "react";
import {initialRegisterBody} from "./RegisterFormHelper";
import LoadingButton from "@mui/lab/LoadingButton";
import {VisibilityOffOutlined, VisibilityOutlined} from "@mui/icons-material";
import {RootState, useAppDispatch, useAppSelector} from "../../../../store";
import {
    getEmailHelperText,
    getPasswordHelperText,
    getRequiredFieldHelperText,
    getSafeBackendErrorMessage,
    validatePhoneNumber
} from "../../../../util/utils";
import {validateEmail, validatePassword} from "../../../../util/regex";
import Typography from "@mui/material/Typography";
import ResendEmail from "../../../shared/resend-email/ResendEmail";
import {registerUser} from "../../../../store/auth/ActionCreators";
import AuthService from "../../../../api/authService";
import {toastError} from "../../../shared/toast/Toast";
import {registerReset} from "../../../../store/auth/Slice";
import {RoutePaths} from "../../../../util/constants";
import ReactGA from "react-ga4";
import PasswordValidationTooltip from "../../../shared/password-validation-tooltip/PasswordValidationTooltip";
import {useTheme} from "@mui/material/styles";

const RegisterForm = () => {
    const {t} = useTranslation()
    const [form, setForm] = useState<IRegisterUserBody>(initialRegisterBody)
    const dispatch = useAppDispatch()
    const registerState = useAppSelector((state: RootState) => state.authReducer.register)
    const [confirmPassword, setConfirmPassword] = useState("")
    const [termsChecked, setTermsChecked] = useState(false)
    const [showPassword, setShowPassword] = useState({
        newPassword: false,
        repeatPassword: false
    })
    let registerInterval: NodeJS.Timeout
    const [counter, setCounter] = useState(30)
    const [savedEmail, setSavedEmail] = useState("")
    const [showPasswordTooltip, setShowPasswordTooltip] = useState(false)
    const theme = useTheme()

    useEffect(() => {
        dispatch(registerReset())
    }, [dispatch]);

    const scrollToTop = () => window.scrollTo({top: 0, behavior: 'smooth'})

    const handleRegisterClick = () => {
        setSavedEmail(form.email)
        ReactGA.event("sign_up", {method: "Standard"})
        dispatch(registerUser({
            body: {
                ...form,
                full_name: `${form.name.trim()} ${form.surname.trim()}`
            }, interval: registerInterval, setCounter, scrollToTop
        }));
    };

    const handleResendEmailClick = () => {
        setCounter(30)
        AuthService.resendConfirmation({email: savedEmail})
            .then()
            .catch((e) => {
                toastError(getSafeBackendErrorMessage(e.response?.data?.detail))
            })
            .finally(() => {
                registerInterval = setInterval(() => {
                    setCounter((oldCounter) => {
                        if (oldCounter === 1) clearInterval(registerInterval)
                        return oldCounter - 1
                    })
                }, 1000)
            })
    }

    const handleClickShowPassword = (key: 'newPassword' | 'repeatPassword') => {
        setShowPassword((old) => ({...old, [key]: !old[key]}))
    };

    const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setForm((oldForm) => ({
            ...oldForm,
            [e.target.name]: validatePhoneNumber(e.target.name, e.target.value)
                ? oldForm.phone_number
                : e.target.value
        }))
    }

    useEffect(() => {
        if (registerState.success) {
            setForm(initialRegisterBody)
            setConfirmPassword("")
            setTermsChecked(false)
        }
    }, [registerState.success])

    return (
        <Box sx={{mt: 1}}>
            {registerState.success ? (
                <Alert severity="success">
                    {registerState.message}
                    <ResendEmail counter={counter} handleOnSubmitClick={handleResendEmailClick} color='secondary'/>
                </Alert>
            ) : null}
            {registerState.error ? <Alert severity="error">{registerState.message}</Alert> : null}

            <Grid container spacing={2} mt={1}>
                <Grid item xs={12} sm={6} sx={{pt: "0 !important"}}>
                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        color="primary"
                        error={registerState.error && !form.name.length}
                        helperText={getRequiredFieldHelperText(form.name, registerState.error)}
                        value={form.name}
                        onChange={handleFormChange}
                        id="name"
                        label={t('first_name')}
                        name="name"
                        autoComplete="fname"
                        autoFocus
                    />
                </Grid>
                <Grid item xs={12} sm={6} sx={{pt: "0 !important"}}>
                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        color="primary"
                        error={registerState.error && !form.surname.length}
                        helperText={getRequiredFieldHelperText(form.surname, registerState.error)}
                        value={form.surname}
                        onChange={handleFormChange}
                        id="surname"
                        label={t('last_name')}
                        name="surname"
                        autoComplete="lname"
                    />
                </Grid>
            </Grid>

            <TextField
                margin="normal"
                required
                color="primary"
                type="email"
                fullWidth
                id="email"
                value={form.email}
                helperText={getEmailHelperText(form.email, registerState.error)}
                error={registerState.error && !validateEmail(form.email)}
                onChange={handleFormChange}
                label={t('e-mail')}
                name="email"
                autoComplete="email"
            />

            <TextField
                margin="normal"
                fullWidth
                color="primary"
                value={form.phone_number}
                onChange={handleFormChange}
                id="phone_number"
                label={t('phone_number')}
                name="phone_number"
                autoComplete="tel"
                type="tel"
            />

            <PasswordValidationTooltip open={showPasswordTooltip} value={form.password}>
                <FormControl margin="normal" fullWidth variant="outlined">
                    <InputLabel
                        required
                        color="primary"
                        error={!!form.password.length && !validatePassword(form.password)}
                        htmlFor="password"
                    >
                        {t('password')}
                    </InputLabel>

                    <OutlinedInput
                        id="password"
                        name="password"
                        type={showPassword.newPassword ? 'text' : 'password'}
                        value={form.password}
                        error={!!form.password.length && !validatePassword(form.password)}
                        required
                        color="primary"
                        autoComplete="new-password"
                        onChange={handleFormChange}
                        onFocus={() => setShowPasswordTooltip(true)}
                        onBlur={() => setShowPasswordTooltip(false)}
                        endAdornment={
                            <InputAdornment position="end">
                                <IconButton
                                    color={!!form.password.length && !validatePassword(form.password) ? 'error' : 'primary'}
                                    aria-label="toggle password visibility"
                                    onClick={() => handleClickShowPassword("newPassword")}
                                    onMouseDown={(e) => e.preventDefault()}
                                    edge="end"
                                >
                                    {showPassword.newPassword ? <VisibilityOffOutlined/> : <VisibilityOutlined/>}
                                </IconButton>
                            </InputAdornment>
                        }
                        label={t('password')}
                    />
                    {!!form.password.length && !validatePassword(form.password) && (
                        <FormHelperText error>
                            {getPasswordHelperText(form.password, true)}
                        </FormHelperText>
                    )}
                </FormControl>
            </PasswordValidationTooltip>

            <FormControl margin="normal" fullWidth variant="outlined">
                <InputLabel
                    required
                    color="primary"
                    error={!!confirmPassword.length && confirmPassword !== form.password}
                    htmlFor="confirm-password"
                >
                    {t('confirm_password')}
                </InputLabel>
                <OutlinedInput
                    id="confirm-password"
                    name="confirm-password"
                    color="primary"
                    type={showPassword.repeatPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    error={!!confirmPassword.length && confirmPassword !== form.password}
                    required
                    autoComplete="new-password"
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    endAdornment={
                        <InputAdornment position="end">
                            <IconButton
                                aria-label="toggle password visibility"
                                color={!!confirmPassword.length && confirmPassword !== form.password ? 'error' : 'primary'}
                                onClick={() => handleClickShowPassword("repeatPassword")}
                                onMouseDown={(e) => e.preventDefault()}
                                edge="end"
                            >
                                {showPassword.repeatPassword ? <VisibilityOffOutlined/> : <VisibilityOutlined/>}
                            </IconButton>
                        </InputAdornment>
                    }
                    label={t('confirm_password')}
                />
                {!!confirmPassword.length && confirmPassword !== form.password && (
                    <FormHelperText error>
                        {t('passwords_not_match')}
                    </FormHelperText>
                )}
            </FormControl>

            <FormControlLabel
                control={
                    <Checkbox
                        sx={{ml: 1.5, mr: 0.5, color: theme.palette.primary.main}}
                        value={form.subscribed}
                        checked={form.subscribed}
                        color="primary"
                        onChange={() =>
                            setForm((oldForm) => ({...oldForm, subscribed: !oldForm.subscribed}))
                        }
                        size="small"
                    />
                }
                label={<Typography variant="body2" color="primary">{t('subscribe_newsletter')}</Typography>}
            />

            <FormControlLabel
                control={
                    <Checkbox
                        sx={{ml: 1.5, mr: 0.5, color: theme.palette.primary.main}}
                        value={termsChecked}
                        checked={termsChecked}
                        color="primary"
                        onChange={() => setTermsChecked((old) => !old)}
                        size="small"
                    />
                }
                label={
                    <Typography variant="body2" color="primary">
                        <Trans
                            i18nKey="accept_privacy_and_terms"
                            components={[
                                <Link color="primary" href={RoutePaths.PRIVACY} target="_blank"
                                      rel="noopener noreferrer"/>,
                                <Link color="primary" href={RoutePaths.TERMS} target="_blank"
                                      rel="noopener noreferrer"/>
                            ]}
                        /> *
                    </Typography>
                }
            />

            <LoadingButton
                fullWidth
                variant="contained"
                color="primary"
                disabled={
                    !termsChecked ||
                    confirmPassword !== form.password ||
                    !validatePassword(confirmPassword) ||
                    !form.name.trim() ||
                    !form.surname.trim() ||
                    !validateEmail(form.email)
                }
                loading={registerState.loading}
                onClick={handleRegisterClick}
                sx={{mt: 3}}
            >
                {t('register')}
            </LoadingButton>
        </Box>
    )
}

export default RegisterForm
